import {Component, EventEmitter, Inject, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {CdkDragDrop, DragDropModule, moveItemInArray} from "@angular/cdk/drag-drop";
import {SwitchBoard} from "../../services/SwitchBoard";
import {LoadingService} from "../../services/LoadingService";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule} from "@angular/material/dialog";
import {ActivatedRoute, Router} from "@angular/router";
import {QuestionnairesSurveysService} from "../../services/questionnaires-surveys.service";
import {takeUntil} from "rxjs/operators";
import {StateService} from "../../services/StateService";
import {Session} from "../../services/Session";
import {PageTitleService} from "../../services/PageTitle";
import {ManageLabelsService} from "../../services/manageLabels.service";
import {ErrorHandlerService} from "../../services/error-handler.service";
import {v4 as uuid} from 'uuid';
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatNativeDateModule, MatOptionModule} from "@angular/material/core";
import {ScrollingModule} from "@angular/cdk/scrolling";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {TextFieldModule} from "@angular/cdk/text-field";
import {MatInputModule} from "@angular/material/input";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatMenuModule} from "@angular/material/menu";
import {MatSortModule} from "@angular/material/sort";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatTableModule} from "@angular/material/table";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatCardModule} from "@angular/material/card";
import {MatTabsModule} from "@angular/material/tabs";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {MatSelectModule} from "@angular/material/select";
import {MatIconModule} from "@angular/material/icon";
import {MatListModule} from "@angular/material/list";
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatRadioModule} from "@angular/material/radio";
import {MatChipsModule} from "@angular/material/chips";
import {MatBadgeModule} from "@angular/material/badge";
import {MatBottomSheetModule} from "@angular/material/bottom-sheet";
import {MatDividerModule} from "@angular/material/divider";
import {AddEditQuestionComponent} from "../add-edit-question/add-edit-question.component";
import {TabsAddEditDeleteQsComponent} from "../tabs-add-edit-delete-qs/tabs-add-edit-delete-qs.component";

declare const _: any;

export interface DialogData {
  parentQuestionnaireId: string;
  parentOrg: string;
  instanceSections: Array<any>;
  instanceQuestionnaireId: string;
}

@Component({
  selector: 'app-create-qs',
  standalone: true,
  imports: [CommonModule, FormsModule, AddEditQuestionComponent, TabsAddEditDeleteQsComponent,
    MatCardModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatSidenavModule,
    MatTabsModule,
    MatIconModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatBadgeModule,
    MatTooltipModule,
    MatMenuModule,
    ScrollingModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatBottomSheetModule,
    MatSlideToggleModule,
    MatOptionModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    DragDropModule,
    MatTableModule,
    MatSortModule,
    MatDialogModule,
    MatChipsModule,
    MatDividerModule,
    TextFieldModule,
    MatExpansionModule,
    MatAutocompleteModule,
    MatSelectModule],
  templateUrl: './create-qs.component.html',
  styleUrl: './create-qs.component.scss'
})
export class CreateQsComponent implements OnInit, OnDestroy {
  tabs: Array<any> = [];
  org = '';
  userId = '';
  questions: Array<any> = [];
  allQuestions: Array<any> = [];
  editedIndex = 0;
  clientLabels: Array<any> = [];
  questionnaireId = '';
  questionnaireName = '';
  questionBucket = '';
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  newTab: boolean;
  hasUpdates: boolean;
  parentQuestionnaireId: string;
  // set only from the backend's parentOrg on load; never guessed. The import
  // action stays disabled until the backend tells us the real master org.
  masterOrg = '';
  allowEditing = true;
  childSections: Array<any> = [];
  isMaster: boolean;
  popupDialog: any;

  constructor(
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private switchBoard: SwitchBoard,
    private stateService: StateService,
    private router: Router,
    private _route: ActivatedRoute,
    private labelsService: ManageLabelsService,
    private qsService: QuestionnairesSurveysService,
    private loadingService: LoadingService,
    private titleService: PageTitleService,
    private errorHandler: ErrorHandlerService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
  }

  ngOnInit(): void {
    this.init();
    if (this.data.parentOrg) {
      this.allowEditing = false;
      this.childSections = this.data.instanceSections;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  init(): void {
    this.org = Session.getOrg() ? Session.getOrg() : '';
    this.userId = Session.getUser().id ? Session.getUser().id : '';
    this.getQuestions();
    this.switchBoard.onPageChangeListener.next({
      url: this._route.url,
      name: 'Manage Questionnaire',
      id: 'QUESTIONNAIRE'
    });
    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(title => {
      const prevState = this.stateService.getPrevState();
      this.stateService.pop();
      this.router.navigate([prevState.url]);
    });
  }

  getLabelsByType(type): void {
    this.loadingService.triggerLoadingEvent(true);
    this.labelsService
      .getLabelType(type, this.org, false)
      .subscribe(response => {
        this.clientLabels = response.labels || [];
      }, error => {
        this.handleError(error);
      });
  }

  getQuestions(): void {
    this.loadingService.triggerLoadingEvent(true);
    const org = this.data.parentOrg ? this.data.parentOrg : this.org;
    this.qsService.getQuestionnaire(org, this.userId).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.questionnaireId = response ? response.questionnaireId : '';
      this.tabs = response ? response.sections ? response.sections : [] : [];
      this.allQuestions = response ? response.questions ? response.questions : [] : [];
      this.filter(0);
      this.questionnaireName = response && response.questionnaireName ? response.questionnaireName.replace('TPIQ', '').trim(): '';
      this.questionBucket = response ? response.questionBucket ? response.questionBucket : '' : '';
      this.titleService.setPageTitle(`${this.questionnaireName}`);
      this.hasUpdates = response.hasUpdates;
      this.parentQuestionnaireId = response.parentQuestionniareId;
      // backend returns the master org so the import dialog no longer
      // hard-codes it (prod master is trialtaxplaniq, not master-taxplaniq).
      // Leave it blank when absent so import stays disabled rather than
      // guessing a wrong org.
      this.masterOrg = response.parentOrg ? response.parentOrg : '';
      this.isMaster = response.isMaster;
    }, error => {
      this.handleError(error);
    });
  }

  filter(index): void {
    const tabId = this.tabs[index] ? this.tabs[index].id : '';
    this.editedIndex = index;
    this.questions = _.filter(this.allQuestions, (question) => {
      return question.sectionId === tabId;
    });
  }

  getQsObj(): any {
    return {
      id: '',
      question: '',
      questionHtml: '',
      questionDocs: [],
      questionNo: '',
      type: 'Short Answer',
      placeholder: 'Short Answer Text',
      multipleOptions: [{
        id: uuid(),
        value: '',
      }],
      checkboxes: [{
        id: uuid(),
        value: '',
        checked: false
      }],
      rollOn: false,
      mandatory: false,
      appliedTo: [],
      questionBucket: '',
      sectionId: '',
      sectionName: ''
    };
  }

  spliceQuestion(index): void {
    this.questions.splice(index, 1);
  }

  drop(event: CdkDragDrop<string[]>): void {
    const obj = {
      questionId: this.questions[event.previousIndex].id,
      oldOrder: event.previousIndex + 1,
      newOrder: event.currentIndex + 1,
      questionBucket: this.questionBucket,
      sectionId: this.questions[event.previousIndex].sectionId
    };
    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
    this.qsService.questionDragDrop(this.userId, this.org, this.questionnaireId, obj)
      .subscribe((response) => {}, error => {
        moveItemInArray(this.questions, event.currentIndex, event.previousIndex);
        this.handleError(error);
      });
  }

  tabsdrop(event: CdkDragDrop<string[]>): void {
    const obj = {
      oldOrder: event.previousIndex + 1,
      newOrder: event.currentIndex + 1,
      sectionId: this.tabs[event.previousIndex].id
    };
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    this.qsService.tabDragDrop(this.userId, this.org, this.questionnaireId, obj)
      .subscribe((response) => {}, error => {
        moveItemInArray(this.tabs, event.currentIndex, event.previousIndex);
        this.handleError(error);
      });
  }

  getSectionObj(): any {
    return {
      id: '',
      is_default: false,
      name: '',
      order: 1
    };
  }

  onAddNewTab(): void {
    this.newTab = true;
    const dialogRef = this.dialog.open(TabsAddEditDeleteQsComponent, {
      data: {
        action: 'add',
        index: this.tabs.length + 1,
        questionnaireId: this.questionnaireId,
        tabObj: this.getSectionObj()}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!_.isEmpty(result.response)) {
        this.tabs.push(result.response);
        setTimeout(() => {
          this.editedIndex = this.tabs.length - 1;
          this.newTab = false;
        }, 1000);
      }
    });
  }

  editTabName(index, tab): void {
    const dialogRef = this.dialog.open(TabsAddEditDeleteQsComponent, {
      data: {action: 'edit', tabObj: tab, index, questionnaireId: this.questionnaireId}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!_.isEmpty(result.response)) {
        this.tabs[index] = result.response;
        this.editedIndex = result.index;
      }
    });
  }

  deleteTabName(index, tab): void {
    const dialogRef = this.dialog.open(TabsAddEditDeleteQsComponent, {
      data: {action: 'delete', tabObj: tab, index, questionnaireId: this.questionnaireId}
    });

    dialogRef.afterClosed().subscribe(result => {
      this.tabs.splice(result.index, 1);
      this.editedIndex = result.index - 1;
    });
  }

  onAction(action, idx): void {
    if (this.allowEditing){
      const newObj = this.getQsObj();
      newObj.sectionId = this.tabs[this.editedIndex].id;
      newObj.sectionName = this.tabs[this.editedIndex].name;
      newObj.questionBucket = this.questionBucket;
      const dialogRef = this.dialog.open(AddEditQuestionComponent, {
        data: {
          action,
          questionnaireId: this.questionnaireId,
          questionObj: action === 'add' ? newObj : this.questions[idx],
          index: idx,
          clientLabels: this.clientLabels,
        },
        minWidth: 850
      });

      dialogRef.afterClosed().subscribe(result => {
        this.checkAction(result);
      });
    }
  }

  onDeleteAction(action, idx): void {
    const dialogRef = this.dialog.open(AddEditQuestionComponent, {
      data: {
        action,
        questionnaireId: this.questionnaireId,
        questionObj: this.questions[idx],
        index: idx,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.checkAction(result);
    });
  }

  checkAction(result): void {
    if (result.action === 'add' && !_.isEmpty(result)) {
      this.questions.splice(result.index, 0, result.questionObj);
      this.allQuestions.push(result.questionObj);
    } else if (result.action === 'edit' && !_.isEmpty(result)) {
      this.questions[result.index] = result.questionObj;
      const idx = _.findIndex(this.allQuestions, {id: result.questionObj.id});
      if (idx !== -1) {
        this.allQuestions[idx] = result.questionObj;
      }
    } else if (result.action === 'delete' && !_.isEmpty(result)) {
      this.questions.splice(result.index , 1);
      const idx = _.findIndex(this.allQuestions, {id: result.questionObj.id});
      if (idx !== -1) {
        this.allQuestions.splice(idx, 1);
      }
    }
  }

  handleError(error): void {
    this.loadingService.triggerLoadingEvent(false);
    this.errorHandler.handleError(error);
  }

  showParentQuestionnaire() {
    if (!this.masterOrg) {
      // No master org means we cannot resolve the parent questionnaire. Report
      // it instead of requesting a guessed org that returns the wrong content.
      this.switchBoard.onNewToast.next({
        toastId: Math.floor(Math.random() * 1000),
        type: 'info',
        message: 'Import is not available: the parent questionnaire org is unknown.',
        toastClass: 'toast-info',
        duration: 4000
      });
      return;
    }
    const base = this;
    this.popupDialog = this.dialog.open(CreateQsComponent, {
      data: {
        parentQuestionnaireId: this.parentQuestionnaireId,
        parentOrg: this.masterOrg,
        instanceSections: this.tabs,
        instanceQuestionnaireId: this.questionnaireId
      },
      height: '85vh',
      width: '85vw'
    });
    this.popupDialog.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
    });
  }

  importQuestion(section, question){
    const selectedSection = _.find(this.data.instanceSections, { id : section.value});
    question.id = "";
    question.sectionId = selectedSection.id;
    question.sectionName = selectedSection.name;
    this.loadingService.triggerLoadingEvent(true);
    this.qsService.addNewQuestion(this.userId, this.org, this.data.instanceQuestionnaireId, question)
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.popupDialog.close();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.errorHandler.handleError(error);
      });
  }

  publishQuestionnaire(hasUpdates) {
    this.loadingService.triggerLoadingEvent(true);
    this.qsService.markUpdates(this.userId, this.org, this.questionnaireId, hasUpdates)
      .subscribe((response) => {
        this.hasUpdates = false;
        this.loadingService.triggerLoadingEvent(false);
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.errorHandler.handleError(error);
      });
  }

}
